import { Outlet, useOutletContext, useParams, useSearchParams } from 'react-router-dom'

import { F0Box } from '@factorialco/f0-react'
import {
  EditExpenseBudgetFragment,
  FinanceBudgetDetailRevampPageDocument,
  FinanceBudgetsDetailBudgetFragment,
  FinanceBudgetsBudgetAmountWidgetsFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { BudgetAmountWidgets } from 'modules/finance/components/Budgets/BudgetDetailDataCollection/AmountWidgets'
import { EditBudgetSidepanel } from 'modules/finance/components/Budgets/ExpenseBudgetDetailDataCollection/EditBudgetSidepanel'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export interface ExpenseBudgetDetailContext {
  budget: FinanceBudgetsDetailBudgetFragment & EditExpenseBudgetFragment
  amounts: FinanceBudgetsBudgetAmountWidgetsFragment
  locale: string
  refetch: () => Promise<unknown>
}

const ExpenseBudgetDetailPage = () => {
  const { budgetId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    access: { locale },
  } = useCurrentSession()

  const { data, loading, error, refetch } = useQuery(FinanceBudgetDetailRevampPageDocument, {
    variables: { budgetId: budgetId ?? '' },
  })

  const budget = data?.finance?.budget
  const amounts = data?.finance?.budgetAmountsConnection?.nodes[0]

  const isEditOpen = searchParams.get('modal') === 'edit'

  const handleEditClose = () => {
    setSearchParams({})
  }

  const handleEditSuccess = async () => {
    await refetch()
    setSearchParams({})
  }

  const context: ExpenseBudgetDetailContext | null =
    budget && amounts ? { budget, amounts, locale, refetch } : null

  return (
    <ResponsivePage.Body loading={loading} error={error}>
      {context && (
        <F0Box display="flex" flexDirection="column" gap="xl">
          <BudgetAmountWidgets budget={context.budget} initialAmounts={context.amounts} />
          <Outlet context={context} />
        </F0Box>
      )}
      {isEditOpen && budget && (
        <EditBudgetSidepanel
          budget={budget}
          onClose={handleEditClose}
          onSuccess={handleEditSuccess}
        />
      )}
    </ResponsivePage.Body>
  )
}

export const useExpenseBudgetDetailContext = () => {
  return useOutletContext<ExpenseBudgetDetailContext>()
}
